// ---------------------------------------------------------------------------
// YouAreYou (uru) — Verified Human Public Profile Page
//
// Public page rendered at /p/:code
//
// Shows a user's verified identity certificate to anyone with the link.
// Intended as a shareable proof-of-humanity card — e.g. a nanny sharing
// their verified status with prospective employers, or a freelancer
// demonstrating they are a real, verified person.
//
// Layout (top to bottom):
//   Nav header    — logoBrand PNG (link to /), "Verified Profile" badge
//   Hero section  — big "Verified Human" H1 + subheading (outside card)
//   Person card   — avatar, name, "Verified Human by YouAreYou", badges, explainer
//   CTA block     — "Verify you are human" button (outside card)
//   Footer        — Powered by MojoVerify
//
// Data:
//   GET /api/uru/connect/resolve/{code}  (docs/api.md:357-388)
// ---------------------------------------------------------------------------

import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { resolveConnectionLink } from "@/api/endpoints";
import type { ResolveConnectionData, RequestorVerifications } from "@/types/api";
import Icon from "@/components/ui/Icon";
import LogoSpinner from "@/components/ui/LogoSpinner";
import logoBrand from "@/assets/logo-brand-transparent2.jpg";

// ---------------------------------------------------------------------------
// Badge config
// ---------------------------------------------------------------------------

interface BadgeConfig {
  key: keyof RequestorVerifications;
  icon: string;
  label: string;
}

const BADGE_CONFIGS: BadgeConfig[] = [
  { key: "is_gov_id_verified",        icon: "badge",       label: "Government ID" },
  { key: "is_liveness_verified",      icon: "face",        label: "Liveness Scan" },
  { key: "is_phone_verified",         icon: "smartphone",  label: "Phone Verified" },
  { key: "is_email_verified",         icon: "mail",        label: "Email Verified" },
  { key: "is_ssn_verified",           icon: "fingerprint", label: "SSN Verified" },
  { key: "is_address_verified",       icon: "home",        label: "Address Verified" },
  { key: "is_ofac_verified",          icon: "gpp_good",    label: "OFAC Cleared" },
  { key: "is_adverse_media_verified", icon: "newspaper",   label: "Adverse Media" },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type PageState = "loading" | "unavailable" | "success";

export default function VerifiedHumanPage() {
  const { code } = useParams<{ code: string }>();
  const [pageState, setPageState] = useState<PageState>("loading");
  const [data, setData] = useState<ResolveConnectionData | null>(null);
  const [avatarError, setAvatarError] = useState(false);

  useEffect(() => {
    if (!code) {
      setPageState("unavailable");
      return;
    }
    resolveConnectionLink(code)
      .then((result) => {
        setData(result);
        setPageState("success");
      })
      .catch(() => {
        setPageState("unavailable");
      });
  }, [code]);

  // ---- Nav header (shared across all states) ----
  const nav = (
    <header className="sticky top-0 z-50 border-b border-gray-200 bg-white/80 backdrop-blur-md">
      <div className="mx-auto flex max-w-2xl items-center justify-between px-4 py-3 sm:px-6">
        <Link to="/">
          <img src={logoBrand} alt="YouAreYou" className="h-8 w-auto" />
        </Link>
        <span className="rounded-full bg-green-50 px-3 py-1 text-xs font-bold text-green-700 ring-1 ring-green-200">
          Verified Profile
        </span>
      </div>
    </header>
  );

  // ---- Loading ----
  if (pageState === "loading") {
    return (
      <div className="flex min-h-screen flex-col bg-gradient-to-br from-white to-blue-50">
        {nav}
        <div className="flex flex-1 items-center justify-center">
          <LogoSpinner size="md" label="Loading profile…" />
        </div>
      </div>
    );
  }

  // ---- Profile unavailable ----
  if (pageState === "unavailable" || !data) {
    return (
      <div className="flex min-h-screen flex-col bg-gradient-to-br from-white to-blue-50">
        {nav}
        <div className="flex flex-1 flex-col items-center justify-center px-4 text-center">
          <div className="w-full max-w-sm overflow-hidden rounded-3xl border border-gray-200 bg-white p-10 shadow-sm">
            <div className="mb-4 flex justify-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
                <Icon name="person_off" className="text-3xl text-gray-400" />
              </span>
            </div>
            <h1 className="text-xl font-bold text-text-primary">Profile Not Available</h1>
            <p className="mt-2 text-sm text-text-secondary">
              This verified profile is unavailable or has been disabled by its owner.
            </p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 rounded-xl bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
            >
              Go to YouAreYou
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ---- Success ----
  const activeBadges = BADGE_CONFIGS.filter(
    (b) => data.requestor_verifications[b.key] === true,
  );

  const avatarUrl = data.requestor_avatar || data.requestor_face_url;
  const isVideo =
    avatarUrl &&
    (avatarUrl.endsWith(".webm") ||
      avatarUrl.endsWith(".mp4") ||
      avatarUrl.endsWith(".gif"));

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-blue-50">
      {nav}

      <main className="mx-auto max-w-2xl px-4 pb-16 pt-10 sm:px-6">

        {/* ---- Hero title (outside the card) ---- */}
        <div className="mb-8 text-center">
          <div className="mb-3 flex items-center justify-center gap-2">
            <Icon name="verified" className="text-3xl text-primary-500" />
            <h1 className="text-4xl font-extrabold tracking-tight text-text-primary sm:text-5xl">
              Verified Human
            </h1>
          </div>
          <p className="text-base text-text-secondary">
            Identity independently verified by{" "}
            <span className="font-semibold text-primary-500">YouAreYou</span>
          </p>
        </div>

        {/* ---- Person certificate card ---- */}
        <div className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-xl shadow-blue-100/40">

          {/* Blue header strip */}
          <div className="bg-primary-500 px-6 pb-20 pt-10" />

          {/* Avatar — overlaps the blue strip */}
          <div className="-mt-14 flex justify-center px-6">
            <div className="relative">
              {avatarUrl && !avatarError ? (
                isVideo ? (
                  <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="h-28 w-28 rounded-full border-4 border-white object-cover shadow-md"
                    aria-label={`Liveness video for ${data.requestor_name}`}
                  >
                    <source src={avatarUrl} />
                  </video>
                ) : (
                  <img
                    src={avatarUrl}
                    alt={data.requestor_name}
                    onError={() => setAvatarError(true)}
                    className="h-28 w-28 rounded-full border-4 border-white object-cover shadow-md"
                  />
                )
              ) : (
                <div className="flex h-28 w-28 items-center justify-center rounded-full border-4 border-white bg-primary-100 shadow-md">
                  <Icon name="person" className="text-5xl text-primary-400" />
                </div>
              )}
              <span
                className="absolute bottom-0.5 right-0.5 flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-green-500 shadow"
                aria-label="Verified"
              >
                <Icon name="check" className="text-base text-white" />
              </span>
            </div>
          </div>

          {/* Name + credential subtitle */}
          <div className="mt-4 px-6 text-center">
            <h2 className="text-2xl font-extrabold text-text-primary">
              {data.requestor_name}
            </h2>
            <p className="mt-1 text-sm font-medium text-text-secondary">
              Verified Human by{" "}
              <span className="font-semibold text-primary-500">YouAreYou</span>
            </p>
          </div>

          {/* Verification badges */}
          {activeBadges.length > 0 ? (
            <div className="mt-6 px-6">
              <p className="mb-3 text-[11px] font-extrabold uppercase tracking-widest text-text-muted">
                Verified Factors
              </p>
              <div className="flex flex-wrap gap-2">
                {activeBadges.map((badge) => (
                  <span
                    key={badge.key}
                    className="flex items-center gap-1.5 rounded-full bg-green-50 px-3 py-1.5 text-xs font-semibold text-green-700 ring-1 ring-green-200"
                  >
                    <Icon name={badge.icon} className="text-sm" />
                    {badge.label}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {/* Divider */}
          <div className="mx-6 my-6 border-t border-gray-100" />

          {/* Explainer */}
          <div className="px-6 pb-6">
            <div className="rounded-2xl bg-blue-50 p-4 ring-1 ring-blue-100">
              <div className="mb-2 flex items-center gap-2">
                <Icon name="info" className="text-base text-primary-500" />
                <p className="text-sm font-bold text-primary-700">
                  What is a Verified Human?
                </p>
              </div>
              <p className="text-xs leading-relaxed text-text-secondary">
                A Verified Human on YouAreYou has completed independent identity
                checks — government-issued ID, liveness scan, and phone
                verification. This profile gives you confidence that you're
                dealing with a real, verified person — not a bot, an
                impersonator, or a fake account.
              </p>
            </div>
          </div>
        </div>

        {/* ---- CTA block (outside the card) ---- */}
        <div className="mt-6">
          <Link
            to="/register"
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary-500 py-4 text-sm font-bold text-white shadow-sm transition-colors hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2"
          >
            <Icon name="verified_user" className="text-lg" />
            Verify you are human
          </Link>
          <p className="mt-2.5 text-center text-xs text-text-muted">
            Get your own verified identity profile. Free to start.
          </p>
        </div>

        {/* ---- Footer ---- */}
        <div className="mt-8 text-center">
          <p className="text-xs text-text-muted">
            Powered by{" "}
            <span className="font-semibold text-text-secondary">MojoVerify</span>
          </p>
        </div>
      </main>
    </div>
  );
}
